/**
 * Hit-test the sidebar for campaign rows at a screen pointer.
 * Uses geometry (not elementFromPoint) so drag overlays don't block detection.
 *
 * PERFORMANCE: rects are cached for a short TTL. During a drag this runs on every
 * pointer event; measuring the sidebar + every row each time forces layout reflows
 * (especially interleaved with highlight class changes) and tanks the frame rate.
 * Rows don't move mid-drag except on sidebar scroll — callers invalidate on scroll
 * and the TTL bounds any residual staleness.
 */

const CACHE_TTL_MS = 150;

let geometryCache = null; // { ts, sidebarRect, rows: [{ id, rect }] }

const isTemplateRow = (el) => el?.getAttribute?.('data-sidebar-is-template') === 'true';

const getVisibleSidebar = () => {
  const inLayout = document.querySelector('.app-layout > .sidebar:not(.sidebar--collapsed)');
  if (inLayout) return inLayout;
  return document.querySelector('.sidebar:not(.sidebar--collapsed)');
};

const buildGeometry = () => {
  const sidebar = getVisibleSidebar();
  if (!sidebar) {
    return { ts: performance.now(), sidebarRect: null, rows: [] };
  }

  const rows = [];
  sidebar.querySelectorAll('[data-sidebar-campaign-id]').forEach((row) => {
    if (isTemplateRow(row)) return;
    rows.push({
      id: row.getAttribute('data-sidebar-campaign-id'),
      rect: row.getBoundingClientRect(),
    });
  });

  return { ts: performance.now(), sidebarRect: sidebar.getBoundingClientRect(), rows };
};

const getGeometry = () => {
  if (!geometryCache || performance.now() - geometryCache.ts > CACHE_TTL_MS) {
    geometryCache = buildGeometry();
  }
  return geometryCache;
};

/** Drop the cached rects (call on drag start and on scroll during a drag). */
export function invalidateSidebarHitTestCache() {
  geometryCache = null;
}

const rectContains = (rect, x, y) =>
  !!rect && x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

/**
 * @returns {boolean} True if pointer is anywhere over the visible sidebar
 */
export function isPointerOverSidebar(clientX, clientY) {
  return rectContains(getGeometry().sidebarRect, clientX, clientY);
}

/**
 * @param {number} clientX
 * @param {number} clientY
 * @returns {string|null} Campaign sprint id under the pointer, or null
 */
export function findSidebarCampaignAtPoint(clientX, clientY) {
  const { sidebarRect, rows } = getGeometry();
  if (!rectContains(sidebarRect, clientX, clientY)) return null;

  // Reverse order: last (deepest / most nested) row in the DOM wins
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    if (rectContains(rows[i].rect, clientX, clientY)) {
      return rows[i].id || null;
    }
  }

  return null;
}

export default findSidebarCampaignAtPoint;
